import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DollarSign, Calendar, Users, FileText, CreditCard, TrendingUp, AlertCircle, Hash } from 'lucide-react';

export const DashboardPage = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [totalGames, setTotalGames] = useState(0);
  const [recentGames, setRecentGames] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, gamesRes, invoicesRes, counterRes] = await Promise.all([
          api().get('/dashboard/stats'),
          api().get('/games'),
          api().get('/invoices'),
          api().get('/games/counter/total')
        ]);
        setStats(statsRes.data);
        setRecentGames(gamesRes.data.slice(0, 5));
        setRecentInvoices(invoicesRes.data.slice(0, 5));
        setTotalGames(counterRes.data.total_games);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [api]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-orange-900/30 text-orange-400 border-orange-900',
      approved: 'bg-purple-900/30 text-purple-400 border-purple-900',
      scheduled: 'bg-blue-900/30 text-blue-400 border-blue-900',
      in_progress: 'bg-yellow-900/30 text-yellow-400 border-yellow-900',
      completed: 'bg-green-900/30 text-green-400 border-green-900',
      cancelled: 'bg-red-900/30 text-red-400 border-red-900',
      draft: 'bg-zinc-800 text-zinc-300 border-zinc-700',
      sent: 'bg-blue-900/30 text-blue-400 border-blue-900',
      paid: 'bg-green-900/30 text-green-400 border-green-900',
      overdue: 'bg-red-900/30 text-red-400 border-red-900'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Revenue This Month', 
      value: formatCurrency(stats?.total_revenue_month || 0), 
      icon: DollarSign,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-900/20'
    },
    { 
      label: 'Games Scheduled', 
      value: stats?.total_games_scheduled || 0, 
      icon: Calendar,
      color: 'text-blue-400',
      bgColor: 'bg-blue-900/20'
    },
    { 
      label: 'Total Games (All Time)', 
      value: totalGames, 
      icon: Hash,
      color: 'text-purple-400',
      bgColor: 'bg-purple-900/20'
    },
    { 
      label: 'Officials Assigned', 
      value: stats?.total_officials_assigned || 0, 
      icon: Users,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-900/20'
    },
    { 
      label: 'Outstanding Invoices', 
      value: formatCurrency(stats?.outstanding_invoices || 0), 
      icon: FileText,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-900/20'
    },
    { 
      label: 'Payroll Owed', 
      value: formatCurrency(stats?.payroll_owed || 0), 
      icon: CreditCard,
      color: 'text-red-400',
      bgColor: 'bg-red-900/20'
    }
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of your referee management operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <div 
            key={index}
            data-testid={`stat-card-${index}`}
            className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-zinc-700 transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{stat.label}</p>
                <p className={`text-2xl font-heading font-bold ${stat.color}`}>{stat.value}</p>
              </div>
              <div className={`p-2 rounded-sm ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Games */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-bold uppercase tracking-wide">Recent Games</h2>
            <TrendingUp className="w-5 h-5 text-zinc-500" />
          </div>
          
          {recentGames.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-zinc-500">
              <Calendar className="w-8 h-8 mb-2" />
              <p>No games scheduled yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentGames.map((game) => (
                <div 
                  key={game.id}
                  className="flex items-center justify-between p-3 border-l-4 border-l-blue-500 bg-zinc-900 border-y border-r border-zinc-800"
                >
                  <div>
                    <p className="font-medium">{game.tournament_event}</p>
                    <p className="text-sm text-zinc-500">{game.division} • {formatDate(game.game_date)}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(game.status)}`}>
                    {game.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-bold uppercase tracking-wide">Recent Invoices</h2>
            <FileText className="w-5 h-5 text-zinc-500" />
          </div>
          
          {recentInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-zinc-500">
              <FileText className="w-8 h-8 mb-2" />
              <p>No invoices created yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentInvoices.map((invoice) => (
                <div 
                  key={invoice.id}
                  className="flex items-center justify-between p-3 border-l-4 border-l-emerald-500 bg-zinc-900 border-y border-r border-zinc-800"
                >
                  <div>
                    <p className="font-mono text-sm">{invoice.invoice_number}</p>
                    <p className="text-sm text-zinc-500">{invoice.client_info?.organization_name || 'Unknown Client'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(invoice.total_due)}</p>
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {stats?.outstanding_invoices > 0 && (
        <div className="flex items-center gap-3 p-4 bg-yellow-900/20 border border-yellow-900 text-yellow-400 rounded-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">
            You have <strong>{formatCurrency(stats.outstanding_invoices)}</strong> in outstanding invoices. Consider sending payment reminders.
          </p>
        </div>
      )}
    </div>
  );
};
